import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, ArrowLeft, Loader2 } from 'lucide-react';
import { Modal } from './ui/Modal';
import StructuredAddressForm, { AddressFormValues, emptyAddressForm } from './ui/StructuredAddressForm';
import { ClientAddress, getClientAddresses, createClientAddress, buildFullAddress } from '../services/orderLinkAPI';

interface AddressSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (address: ClientAddress) => void;
  linkId: string;
}

const AddressSelectionModal: React.FC<AddressSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  linkId,
}) => {
  const [view, setView] = useState<'list' | 'new'>('list');
  const [addresses, setAddresses] = useState<ClientAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState<AddressFormValues>(emptyAddressForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof AddressFormValues, string>>>({});

  const loadAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getClientAddresses(linkId);
      setAddresses(data.addresses);
      if (data.addresses.length === 0) {
        setView('new');
      }
    } catch (err) {
      console.error('Erro ao carregar enderecos:', err);
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  useEffect(() => {
    if (isOpen) {
      setView('list');
      setFormValues(emptyAddressForm);
      setFormErrors({});
      loadAddresses();
    }
  }, [isOpen, loadAddresses]);

  const handleFormChange = (field: keyof AddressFormValues, value: string) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof AddressFormValues, string>> = {};
    if (!formValues.rua.trim()) errors.rua = 'Obrigatorio';
    if (!formValues.numero.trim()) errors.numero = 'Obrigatorio';
    if (!formValues.bairro.trim()) errors.bairro = 'Obrigatorio';
    if (!formValues.cidade.trim()) errors.cidade = 'Obrigatorio';
    if (!formValues.estado.trim()) errors.estado = 'Obrigatorio';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const result = await createClientAddress(linkId, {
        rua: formValues.rua.trim(),
        numero: formValues.numero.trim(),
        bairro: formValues.bairro.trim(),
        cidade: formValues.cidade.trim(),
        estado: formValues.estado.trim(),
        cep: formValues.cep.replace(/\D/g, '') || undefined,
        complemento: formValues.complemento.trim() || undefined,
        apelido: formValues.apelido.trim() || undefined,
      });
      onSelect(result.address);
      onClose();
    } catch (err) {
      console.error('Erro ao salvar endereco:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAddress = (address: ClientAddress) => {
    onSelect(address);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Endereço de Entrega" size="large">
      <div className="max-h-[70vh] overflow-y-auto">
        {view === 'list' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.length > 0 ? (
                  <>
                    <p className="text-sm text-gray-500 mb-2">Selecione um endereço:</p>
                    {addresses.map(addr => (
                      <button
                        key={addr.id}
                        type="button"
                        onClick={() => handleSelectAddress(addr)}
                        className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-gray-400 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 text-sm">
                                {addr.rua}, {addr.numero}
                              </span>
                              {addr.apelido && (
                                <span
                                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                                  style={{ backgroundColor: 'var(--soft-color)', color: 'var(--text-color)' }}
                                >
                                  {addr.apelido}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {addr.bairro}, {addr.cidade} - {addr.estado}
                            </p>
                            {addr.complemento && (
                              <p className="text-xs text-gray-400 mt-0.5">{addr.complemento}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">Nenhum endereço salvo</p>
                    <p className="text-gray-400 text-xs mt-1">Adicione um endereço para continuar</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setFormValues(emptyAddressForm);
                    setFormErrors({});
                    setView('new');
                  }}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar novo endereço
                </button>
              </div>
            )}
          </>
        )}

        {view === 'new' && (
          <div className="space-y-4">
            {addresses.length > 0 && (
              <button
                type="button"
                onClick={() => setView('list')}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para lista
              </button>
            )}

            <StructuredAddressForm
              values={formValues}
              onChange={handleFormChange}
              errors={formErrors}
              disabled={saving}
              showApelido={true}
            />

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="w-full btn btn-order-link text-[14px] py-3 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar endereco'
              )}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddressSelectionModal;
